// Real-world next steps and reference data for BCH School.
// Unlike the lessons (which are simulated), these link to real BCH tools.

/** BCH address for optional graduate tips. Used by the DonateCard. */
export const DONATE_ADDRESS =
  "bitcoincash:qrfe5r4afrvfyzcldap2fcvetusqz2u0ky8h0y9292";

export interface NextStepLink {
  label: string;
  href: string;
  /** Optional short descriptor to help beginners choose (e.g. "Simple mobile"). */
  hint?: string;
}

export interface NextStep {
  title: string;
  description: string;
  emoji: string;
  /** One or more real-world links (e.g. multiple wallet options). */
  links: NextStepLink[];
}

export const NEXT_STEPS: NextStep[] = [
  {
    title: "Get a real BCH wallet",
    description:
      "Download a free, self-custody wallet and create your first real address. You hold the keys — just like you practiced.",
    emoji: "📲",
    links: [
      { label: "Selene Wallet", href: "https://selene.cash", hint: "Simple mobile" },
      { label: "Paytaca", href: "https://paytaca.com", hint: "Payments & tokens" },
      { label: "Electron Cash", href: "https://electroncash.org", hint: "Desktop" },
    ],
  },
  {
    title: "Accept BCH at your business",
    description:
      "Start taking Bitcoin Cash payments with a free, easy point-of-sale — no fees eating your margins.",
    emoji: "🏪",
    links: [{ label: "Try BCHpay.cash", href: "https://bchpay.cash" }],
  },
  {
    title: "Spend & find BCH near you",
    description:
      "Discover local people and merchants using Bitcoin Cash, and put your new skills to work in the real world.",
    emoji: "🗺️",
    links: [
      { label: "BCHlocal.com", href: "https://bchlocal.com", hint: "Worldwide" },
      {
        label: "Paytaca Map",
        href: "https://map.paytaca.com/",
        hint: "Philippines",
      },
    ],
  },
];

export interface GlossaryTerm {
  term: string;
  definition: string;
}

export const GLOSSARY: GlossaryTerm[] = [
  {
    term: "Bitcoin Cash (BCH)",
    definition:
      "Peer-to-peer electronic cash — digital money you can send directly to anyone, anywhere, with tiny fees and no bank in the middle.",
  },
  {
    term: "Wallet",
    definition:
      "An app that stores your keys (not your coins). It lets you receive, hold, and send BCH.",
  },
  {
    term: "Address",
    definition:
      "A string of letters and numbers (often shown as a QR code) that you share so others can send you BCH. Like an email address for money.",
  },
  {
    term: "Private key",
    definition:
      "The secret that proves the coins are yours and lets you spend them. Never share it with anyone.",
  },
  {
    term: "Recovery phrase (seed phrase)",
    definition:
      "Usually 12 words that back up your wallet. Anyone with these words controls your money, so keep them safe and offline.",
  },
  {
    term: "Self-custody",
    definition:
      "Holding your own keys, so you're in full control of your funds. No company can freeze or lose them — but backing up is your responsibility.",
  },
  {
    term: "Network fee",
    definition:
      "A tiny amount paid to confirm your transaction. On BCH it's based on data size, not the amount sent, so it stays a fraction of a cent.",
  },
  {
    term: "Confirmation",
    definition:
      "When the network permanently records your payment in its shared ledger. More confirmations means more certainty.",
  },
  {
    term: "0-conf (zero-confirmation)",
    definition:
      "Accepting a payment the instant it appears, before a full confirmation. Great for small everyday purchases like coffee.",
  },
  {
    term: "QR code",
    definition:
      "A scannable square barcode that carries an address (and often an amount), so you can pay or get paid without typing.",
  },
  {
    term: "Volatility",
    definition:
      "How much BCH's price moves up and down in your local currency. Only keep in BCH what you're comfortable letting fluctuate.",
  },
  {
    term: "Auto-convert",
    definition:
      "A merchant option that instantly turns received BCH into local currency, keeping revenue stable regardless of price swings.",
  },
  {
    term: "BTC (Bitcoin)",
    definition:
      "The original Bitcoin, often described as 'digital gold.' It shares BCH's roots but prioritizes being a store of value over cheap everyday payments.",
  },
  {
    term: "Peer-to-peer (P2P)",
    definition:
      "Directly between people, with no middleman. BCH lets value move person-to-person over the internet.",
  },
];
